/*
 * Collection-card shape + metal swatch rows.
 *
 * WHAT THIS REPLACES (round 15)
 *
 * snippets/product-card-06.liquid used to inline a <script> into every product
 * card. On a 20-product collection page that meant 20 copies of:
 *   - `new Swiper('.shape-swiper')` and `new Swiper('.metal-swiper')`, both
 *     GLOBAL selectors, so every copy re-initialised every swiper on the page;
 *   - a DOMContentLoaded listener that walked the whole page and fired
 *     `fetch(href + '.js')` for every option anchor.
 *
 * Measured on the dev theme, one collection page: 200 requests, 664 KB, and
 * ~307 s of cumulative request time. Each check downloaded the FULL product
 * JSON - 64 KB for a product that exists - purely to answer "does this handle
 * exist".
 *
 * THE BUG THAT DELETED VALID OPTIONS
 *
 * The old code removed the option on ANY failure:
 *
 *     .then(function (resp) { if (!resp || !resp.ok) link.remove(); })
 *     .catch(function () { link.remove(); })
 *
 * Firing ~200 requests at once gets connections dropped - reproduced with curl:
 * the same handle returns a clean 404 on its own, but 29 of 30 in quick
 * succession fail to connect at all. Every one of those dropped requests
 * deleted a swatch the shopper should have been able to click. That is the
 * "sometimes the metal option gets removed, sometimes the shape option".
 *
 * WHAT THIS DOES INSTEAD
 *
 *   - HEAD, not GET: the answer is the status code, so 64 KB becomes 0 bytes.
 *   - Only a definitive 404/410 removes an option. A dropped connection, a
 *     timeout, a 429, a 5xx - anything that is not proof of absence - leaves
 *     the option alone. Showing one option too many is recoverable; deleting a
 *     real product from the grid is not.
 *   - At most 4 checks in flight, so the burst that caused the drops is gone.
 *   - Handles are deduplicated. The page renders 280 option anchors but only
 *     185 distinct handles.
 *   - Answers are cached in sessionStorage, so paging through a collection or
 *     coming back via the browser's back button costs nothing.
 *   - Cards are only checked once they are near the viewport, so the first
 *     paint pays for the two or three cards actually on screen.
 *
 * The row itself no longer uses Swiper at all - see civia-card-variants.css.
 */
(function () {
  'use strict';

  /* The <script> tag is emitted by the first card of a grid, and a page can
     hold more than one grid (collection + recommendations). Run once. */
  if (window.__civiaCardVariants) return;
  window.__civiaCardVariants = true;

  var CACHE_KEY = 'civia:product-handle-exists';
  var MAX_IN_FLIGHT = 4;
  var NEAR_VIEWPORT = '600px 0px';

  /* ------------------------------------------------------------------ cache */

  var known = {};
  try {
    known = JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
  } catch (e) {
    known = {};
  }

  var persistQueued = false;
  function persist() {
    if (persistQueued) return;
    persistQueued = true;
    setTimeout(function () {
      persistQueued = false;
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(known));
      } catch (e) {
        /* private mode / quota - the cache is an optimisation, not a
           requirement, so losing it changes nothing but speed */
      }
    }, 500);
  }

  /* -------------------------------------------------------- active swatch */

  /* Kept on window: the markup calls it from an inline onclick.
     The old version cleared .active across the WHOLE document, so choosing a
     metal on one card cleared the highlight on every other card in the grid.
     Scoped to the row it was clicked in. */
  window.selectVariant = function (el, type) {
    var row = el.closest('.variant-options');
    var scope = row || document;
    var others = scope.querySelectorAll('.variant-option.' + type);
    for (var i = 0; i < others.length; i++) others[i].classList.remove('active');
    el.classList.add('active');
  };

  /* ------------------------------------------------------- arrow scrolling */

  function syncArrows(row) {
    var wrap = row.closest('.variant-swiper');
    if (!wrap) return;
    var prev = wrap.querySelector('.shape-prev, .metal-prev');
    var next = wrap.querySelector('.shape-next, .metal-next');
    var max = row.scrollWidth - row.clientWidth - 1;
    var atStart = row.scrollLeft <= 0;
    var atEnd = row.scrollLeft >= max || max <= 0;
    if (prev) prev.toggleAttribute('data-scroll-end', atStart);
    if (next) next.toggleAttribute('data-scroll-end', atEnd);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest
      ? e.target.closest('.shape-prev, .shape-next, .metal-prev, .metal-next')
      : null;
    if (!btn) return;
    var wrap = btn.closest('.variant-swiper');
    var row = wrap && wrap.querySelector('.variant-options');
    if (!row) return;
    e.preventDefault();
    var back = btn.classList.contains('shape-prev') || btn.classList.contains('metal-prev');
    var by = Math.max(80, Math.round(row.clientWidth * 0.6));
    row.scrollBy({ left: back ? -by : by, behavior: 'smooth' });
  });

  /* ------------------------------------------------------------- existence */

  var queue = [];
  var queued = {};
  var inFlight = 0;

  function applyVerdict(href) {
    if (known[href] !== 0) return;
    var dead = document.querySelectorAll('a.variant-option[href="' + href + '"]');
    for (var i = 0; i < dead.length; i++) {
      if (dead[i].dataset.self === '1') continue;
      dead[i].remove();
    }
  }

  function pump() {
    while (inFlight < MAX_IN_FLIGHT && queue.length) {
      check(queue.shift());
    }
  }

  function check(href) {
    inFlight++;
    var done = function () {
      inFlight--;
      pump();
    };
    fetch(href + '.js', { method: 'HEAD', credentials: 'same-origin' })
      .then(function (resp) {
        if (resp.status === 404 || resp.status === 410) {
          /* proof of absence - and only this removes anything */
          known[href] = 0;
          persist();
          applyVerdict(href);
        } else if (resp.ok) {
          known[href] = 1;
          persist();
        }
        /* any other status (429, 5xx, opaque): undecided, keep the option */
      })
      .catch(function () {
        /* dropped connection or timeout: undecided, keep the option */
      })
      .then(done, done);
  }

  function scanCard(container) {
    var links = container.querySelectorAll('a.variant-option[href]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (link.dataset.self === '1') continue;
      var href = link.getAttribute('href');
      if (!href) continue;

      if (known[href] === 0) {
        link.remove();
        continue;
      }
      if (known[href] === 1) continue;
      if (queued[href]) continue;

      queued[href] = true;
      queue.push(href);
    }
    pump();
  }

  /* ------------------------------------------------------------------ init */

  /* Deliberately a rect check rather than an IntersectionObserver.
     IO only delivers callbacks while the page is actually being composited, so
     in a backgrounded or non-painting tab the pruning silently never runs - it
     failed exactly that way under test. A rect check answers the same question
     ("is this card near the viewport?") from layout alone, always. */
  function nearViewport(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    var margin = parseInt(NEAR_VIEWPORT, 10) || 600;
    return r.top < window.innerHeight + margin && r.bottom > -margin;
  }

  function scan() {
    var cards = document.querySelectorAll('.variant-section.variant-section-custom');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.dataset.civiaScanned === '1') continue;
      if (!nearViewport(card)) continue;
      card.dataset.civiaScanned = '1';
      scanCard(card);
    }

    var rows = document.querySelectorAll('.variant-swiper .variant-options');
    for (var j = 0; j < rows.length; j++) {
      var row = rows[j];
      if (row.dataset.civiaRow === '1') continue;
      row.dataset.civiaRow = '1';
      syncArrows(row);
      (function (r) {
        r.addEventListener('scroll', function () { syncArrows(r); }, { passive: true });
      })(row);
    }
  }

  /* Throttled on a timer, not requestAnimationFrame. rAF is suspended in a
     background or non-painting tab for exactly the same reason IO is, so an
     rAF-throttled scan silently stops running there - measured. A timer keeps
     firing regardless, and 120ms is well under a scroll's worth of movement. */
  var scanQueued = false;
  function scanSoon() {
    if (scanQueued) return;
    scanQueued = true;
    setTimeout(function () {
      scanQueued = false;
      scan();
    }, 120);
  }

  /* Exposed so anything that injects cards later (wishlist page, quick view)
     can re-run it without reloading. */
  window.civiaCardVariants = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  /* Round 93 - `load` and `resize` re-sync EVERY row, not only new ones.
     scan() never looks at a row twice, so a row first synced before the card
     stylesheet had settled - it is linked from inside the first card, and the
     swatches animated their size - kept that arrow state for good. Measured on
     dev at 1440px: the first six cards' shape rows overflowed by 19px with the
     next arrow marked as the end, so the ninth shape was clipped with nothing
     to say so; firing each row's own scroll event put the arrow back.
     civia-card-variants.css now also stops the size animating. */
  function resyncAll() {
    scan();
    var rows = document.querySelectorAll('.variant-swiper .variant-options');
    for (var i = 0; i < rows.length; i++) syncArrows(rows[i]);
  }

  var resyncQueued = false;
  function resyncSoon() {
    if (resyncQueued) return;
    resyncQueued = true;
    setTimeout(function () {
      resyncQueued = false;
      resyncAll();
    }, 120);
  }

  window.addEventListener('load', resyncAll);
  window.addEventListener('scroll', scanSoon, { passive: true });
  window.addEventListener('resize', resyncSoon, { passive: true });
  document.addEventListener('shopify:section:load', scan);
})();
